import React, { useState, useEffect } from 'react';
import { getContributions } from '../../../services/contributions';
import DataTable from '../../../components/Tables/DataTable';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { 
  Download, Printer, Filter, FileText, Wallet, Calendar,
  Hash, ChevronLeft, ChevronRight, Search, Loader2
} from 'lucide-react';

const ContributionReport = () => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    loadContributions();
  }, [currentPage, pageSize]); // Reload when page changes

  const loadContributions = async () => {
    try {
      setLoading(true);
      // Assuming your service supports query params: ?page=1&limit=10
      const response = await getContributions({ page: currentPage, limit: pageSize });
      
      // Standardize response handling (handle both array fallback and paginated object)
      const data = response.data || response; 
      const list = Array.isArray(data) ? data : data.records || [];
      
      setContributions(list);
      setTotalPages(data.totalPages || 1);
      setTotalRecords(data.total || list.length);
      
      // Calculate total for current view (or fetch grand total from API metadata)
      const total = list.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
      setTotalAmount(response.grandTotal || total);
    } catch (err) {
      console.error("Report generation error:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      key: 'member.member_number', 
      label: 'Member ID',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-[10px]">
            {row?.Member?.member_number?.slice(-2) || '??'}
          </div>
          <span className="font-mono font-bold text-slate-700 tracking-tighter">
            {row?.Member?.member_number || 'N/A'}
          </span>
        </div>
      )
    },
    { 
      key: 'case.type', 
      label: 'Allocation',
      render: (_, row) => {
        const caseType = row?.Case?.type || 'general_fund';
        return (
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
            caseType === 'medical' ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-slate-50 text-slate-700 border-slate-100'
          }`}>
            {caseType.replace('_', ' ')}
          </span>
        );
      }
    },
    { 
      key: 'amount', 
      label: 'Amount', 
      render: (val) => (
        <span className="font-black text-slate-900">{formatCurrency(val)}</span>
      ) 
    },
    { 
      key: 'paid_at', 
      label: 'Date', 
      render: (val) => (
        <span className="text-slate-500 text-xs font-medium">{formatDate(val)}</span>
      )
    },
    { 
      key: 'transaction_ref', 
      label: 'Reference',
      render: (val) => (
        <code className="text-[10px] bg-slate-50 px-2 py-1 rounded text-slate-400 border border-slate-100 uppercase">
          {val || '---'}
        </code>
      )
    },
  ];

  return (
    <div className="p-6 md:p-10 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
      
      {/* 1. HEADER SECTION */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-600 rounded-lg text-white">
              <FileText size={20} />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Ledger v2.4</span>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Financial Reports</h1>
          <p className="text-slate-500 text-sm font-medium">Monitoring all contributions across the organization.</p>
        </div>

        <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
          <button className="p-3 hover:bg-slate-50 rounded-xl text-slate-400 transition-colors"><Printer size={20} /></button>
          <div className="h-6 w-[1px] bg-slate-100" />
          <button className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-lg">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* 2. KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Total Pool</p>
          <h3 className="text-3xl font-black text-slate-900">{formatCurrency(totalAmount)}</h3>
          <div className="mt-4 flex items-center gap-2 text-emerald-500 text-[10px] font-black">
            <Wallet size={12} /> GLOBAL BALANCE
          </div>
        </div>
        <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
          <p className="text-[10px] font-black text-slate-400 uppercase mb-2">Statement Count</p>
          <h3 className="text-3xl font-black text-slate-900">{totalRecords}</h3>
          <p className="mt-4 text-[10px] font-bold text-slate-400">Total processed transactions</p>
        </div>
        <div className="bg-indigo-600 p-6 rounded-[2rem] text-white shadow-xl shadow-indigo-100">
          <p className="text-[10px] font-black text-indigo-200 uppercase mb-2">Active Period</p>
          <h3 className="text-3xl font-black">{new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
          <p className="mt-4 text-[10px] font-bold text-indigo-300">Reporting Live Data</p>
        </div>
      </div>

      {/* 3. FILTER TOOLBAR */}
      <div className="flex flex-col md:flex-row items-center gap-4 bg-white p-4 rounded-[1.5rem] border border-slate-100">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search by Member ID or Reference..." 
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600">
            <Filter size={16} /> Date Range
          </button>
        </div>
      </div>

      {/* 4. DATA TABLE & PAGINATION */}
      <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
        <div className="min-h-[400px] relative">
          <DataTable columns={columns} data={contributions} loading={loading} />
          
          {loading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-10">
              <Loader2 className="animate-spin text-indigo-600" size={40} />
            </div>
          )}
        </div>

        {/* PAGINATION CONTROLS */}
        <div className="px-8 py-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs font-bold text-slate-400">
            Showing <span className="text-slate-900">{(currentPage - 1) * pageSize + 1}</span> to <span className="text-slate-900">{Math.min(currentPage * pageSize, totalRecords)}</span> of <span className="text-slate-900">{totalRecords}</span> entries
          </p>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className="p-2 bg-white border border-slate-200 rounded-lg text-slate-600 disabled:opacity-30 transition-all hover:bg-slate-50"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`h-9 w-9 rounded-lg text-xs font-black transition-all ${
                    currentPage === i + 1 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                      : 'bg-white text-slate-400 border border-slate-200 hover:border-indigo-600'
                  }`}
                >
                  {i + 1}
                </button>
              )).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))}
            </div>

            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className="p-2 bg-white border border-slate-200 rounded-lg text-slate-600 disabled:opacity-30 transition-all hover:bg-slate-50"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributionReport;